import React, { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { HelpBubble } from "../components/HelpBubble";
import { daysUrgencyColor } from "../components/ui/status-colors";
import {
  BarChart3,
  Download,
  Calendar,
  FileText,
  DollarSign,
  TrendingUp,
  Printer,
  AlertTriangle,
  Clock,
  Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";

const COLORS = ["#0f172a", "#00e5ff", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"];

const statusLabels = {
  draft: "Draft", under_review: "Under Review", pending_approval: "Pending Approval",
  approved: "Approved", active: "Active", expired: "Expired",
  terminated: "Terminated", archived: "Archived",
};

export default function ReportsPage() {
  const [reportType, setReportType] = useState("status");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [valueDistribution, setValueDistribution] = useState([]);
  const [approvalCycle, setApprovalCycle] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    setDashboardLoading(true);
    try {
      const [expRes, valRes, cycleRes] = await Promise.all([
        apiClient.get("/analytics/reports/expiring?days=90"),
        apiClient.get("/analytics/reports/value-distribution"),
        apiClient.get("/analytics/reports/approval-cycle"),
      ]);
      setExpiringContracts(expRes.data);
      setValueDistribution(valRes.data);
      setApprovalCycle(cycleRes.data);
    } catch (e) { /* Dashboard data is non-critical for page */ }
    finally { setDashboardLoading(false); }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("report_type", reportType);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      const response = await apiClient.get(`/analytics/reports?${params.toString()}`);
      setReportData(response.data);
      toast.success("Report generated");
    } catch (error) {
      toast.error("Failed to generate report");
    } finally { setLoading(false); }
  };

  const exportReport = (format) => {
    if (!reportData) { toast.error("Generate a report first"); return; }
    const filename = `contract_report_${reportType}_${new Date().toISOString().split("T")[0]}`;
    if (format === "csv") {
      const data = reportData.data || [];
      if (data.length > 0) {
        const headers = Object.keys(data[0]).join(",");
        const rows = data.map((item) => Object.values(item).join(",")).join("\n");
        const blob = new Blob([`${headers}\n${rows}`], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${filename}.csv`; a.click();
        URL.revokeObjectURL(url);
      }
    } else if (format === "json") {
      const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${filename}.json`; a.click();
      URL.revokeObjectURL(url);
    }
    toast.success(`Report exported as ${format.toUpperCase()}`);
  };

  const handlePrint = () => window.print();

  const formatCurrency = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0 }).format(v || 0);

  const chartData = reportData?.data?.map((item) => ({
    name: statusLabels[item._id] || item._id || "Unknown",
    count: item.count || 0,
    value: item.total_value || 0,
  })) || [];

  const daysUntil = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

  return (
    <div className="space-y-6 animate-fade-in print:space-y-4" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Reports & Analytics <HelpBubble tip="Generate on-demand reports — contract register, expiry tracker, compliance status, and approval cycle analysis. Export as CSV, PDF, or HTML." />
          </h1>
          <p className="text-slate-500 mt-1">Generate insights and export contract data</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => exportReport("csv")} className="gap-2 rounded-sm" disabled={!reportData} data-testid="export-csv-btn">
            <Download size={16} /> CSV
          </Button>
          <Button variant="outline" onClick={() => exportReport("json")} className="gap-2 rounded-sm" disabled={!reportData} data-testid="export-json-btn">
            <Download size={16} /> JSON
          </Button>
          <Button variant="outline" onClick={handlePrint} className="gap-2 rounded-sm" data-testid="print-pdf-btn">
            <Printer size={16} /> Print / PDF
          </Button>
        </div>
      </div>

      {/* Analytics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Value Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <DollarSign size={14} /> Value Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardLoading ? (
              <div className="h-[200px] flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
            ) : valueDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={valueDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "4px", fontSize: "12px" }} />
                  <Bar dataKey="count" fill="#0f172a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
          </CardContent>
        </Card>

        {/* Approval Cycle */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <TrendingUp size={14} /> Approval Cycle Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardLoading ? (
              <div className="h-[200px] flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
            ) : approvalCycle ? (
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-sm flex-1 text-center">
                    <p className="text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{approvalCycle.average_days}d</p>
                    <p className="text-xs text-slate-500">Avg. Cycle</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-sm flex-1 text-center">
                    <p className="text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{approvalCycle.total_completed}</p>
                    <p className="text-xs text-slate-500">Completed</p>
                  </div>
                </div>
                {approvalCycle.data?.length > 0 && (
                  <ResponsiveContainer width="100%" height={100}>
                    <LineChart data={approvalCycle.data}>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ fontSize: "11px" }} />
                      <Line type="monotone" dataKey="avg_days" stroke="#00e5ff" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
          </CardContent>
        </Card>

        {/* Expiring Contracts */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <AlertTriangle size={14} /> Expiring Soon (90d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardLoading ? (
              <div className="h-[200px] flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
            ) : expiringContracts.length > 0 ? (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {expiringContracts.map((c, i) => {
                  const days = daysUntil(c.expiration_date);
                  return (
                    <div key={c.id || `exp-${i}`} className="flex items-center justify-between p-2 border rounded-sm text-sm">
                      <div className="truncate flex-1 mr-2">
                        <p className="font-medium truncate">{c.title}</p>
                        <p className="text-xs text-slate-500">{c.contracting_party?.legal_name}</p>
                      </div>
                      <Badge className={`text-xs rounded-sm whitespace-nowrap ${daysUrgencyColor(days)}`}>
                        {days}d
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-400 text-center py-8">No contracts expiring in 90 days</p>}
          </CardContent>
        </Card>
      </div>

      {/* Report Generator */}
      <Card className="print:hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Report Generator</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="rounded-sm" data-testid="report-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="status">By Status</SelectItem>
                  <SelectItem value="department">By Department</SelectItem>
                  <SelectItem value="type">By Contract Type</SelectItem>
                  <SelectItem value="category">By Spend Category</SelectItem>
                  <SelectItem value="renewals">Upcoming Renewals</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-sm" data-testid="start-date-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-sm" data-testid="end-date-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">&nbsp;</Label>
              <Button onClick={generateReport} disabled={loading} className="w-full gap-2 rounded-sm bg-slate-900 hover:bg-slate-800 dark:bg-cyan-500 dark:text-slate-900" data-testid="generate-report-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <BarChart3 size={16} />}
                Generate
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Generated Report Results */}
      {reportData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" id="report-output">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
                {reportType === "status" && "Contracts by Status"}
                {reportType === "department" && "Contracts by Department"}
                {reportType === "type" && "Contracts by Type"}
                {reportType === "category" && "Contracts by Spend Category"}
                {reportType === "renewals" && "Upcoming Renewals"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 && reportType !== "renewals" ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={chartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, count }) => `${name}: ${count}`}>
                      {chartData.map((entry, i) => <Cell key={entry.name || `cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : reportType === "renewals" ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {(reportData.data || []).map((c, i) => (
                    <div key={c.id || `renewal-${i}`} className="flex items-center justify-between p-3 border rounded-sm">
                      <div>
                        <p className="font-medium text-sm">{c.title}</p>
                        <p className="text-xs text-slate-500">Renewal: {new Date(c.renewal_date).toLocaleDateString()}</p>
                      </div>
                      <Badge className="rounded-sm">{formatCurrency(c.contract_value)}</Badge>
                    </div>
                  ))}
                  {(reportData.data || []).length === 0 && <p className="text-sm text-slate-400 text-center py-8">No upcoming renewals</p>}
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 && reportType !== "renewals" ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-sm">
                      <p className="text-xs font-mono uppercase text-slate-500 mb-1">Total Count</p>
                      <p className="text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {chartData.reduce((s, i) => s + i.count, 0)}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-sm">
                      <p className="text-xs font-mono uppercase text-slate-500 mb-1">Total Value</p>
                      <p className="text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {formatCurrency(chartData.reduce((s, i) => s + i.value, 0))}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {chartData.map((item, i) => (
                      <div key={item.name || `chart-${i}`} className="flex items-center justify-between p-3 border rounded-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-sm">{item.name}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-sm">{item.count} contracts</p>
                          <p className="text-xs text-slate-500">{formatCurrency(item.value)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : reportType === "renewals" ? (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-sm">
                    <p className="text-xs font-mono uppercase text-slate-500 mb-1">Contracts Expiring Soon</p>
                    <p className="text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{reportData.data?.length || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-sm">
                    <p className="text-xs font-mono uppercase text-slate-500 mb-1">Total Renewal Value</p>
                    <p className="text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {formatCurrency(reportData.data?.reduce((s, c) => s + (c.contract_value || 0), 0) || 0)}
                    </p>
                  </div>
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-8">Generate a report to see statistics</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {!reportData && (
        <Card className="print:hidden">
          <CardContent className="py-16">
            <div className="text-center">
              <BarChart3 size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-2">No report generated yet</p>
              <p className="text-sm text-slate-400">Select a report type and click "Generate" to view analytics</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
